"use client";
import { Input } from "@/ui/index";
import { Label } from "@cmp-src/primitives/label";
import { ConfirmModal } from "@gettactic/components/src/overlay/modals/ConfirmModal";
import { api } from "@/lib/api/api";
import { ISchedule } from "@gettactic/api";
import { useMutation } from "@tanstack/react-query";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState
} from "react";
import toast from "react-hot-toast";
import { useAuthenticated, useInvalidateSchedules } from "@gettactic/hooks";
import { delegatedPermissionPartials } from "@gettactic/helpers";
import { isDelegatedAccessRequiredError } from "@/features/reserve/utils/reservationErrorMessages";

export interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  schedules: ISchedule[];
  onCancelSuccess?: () => void;
}

type DeleteRecurringOptions = "this" | "future" | "all";

const options: { label: string; value: DeleteRecurringOptions }[] = [
  { label: "Only this reservation", value: "this" },
  { label: "This and all the future reservations", value: "future" },
  { label: "All the reservations", value: "all" }
];

export function ReservationCancel({
  setOpen,
  open,
  schedules,
  onCancelSuccess
}: Props) {
  const isSingle = schedules.length === 1;
  const invalidateSchedules = useInvalidateSchedules();
  const [recurringValue, setRecurringValue] =
    useState<DeleteRecurringOptions>("this");

  const onClose = useCallback(() => {
    setRecurringValue("this");
    setOpen(false);
  }, [setOpen]);

  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const isRecurring = schedules.length === 1 && !!schedules[0].recurring_task;

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const withoutDuplicates = schedules.filter((schedule, index, self) => {
        return index === self.findIndex((s) => s.id === schedule.id);
      });
      const promises = withoutDuplicates.map((schedule) => {
        const res = api.client.schedules.delete({
          schedule:
            recurringValue === "all" && schedule.recurring_schedule_id
              ? schedule.recurring_schedule_id
              : schedule.id,
          recurring: isRecurring && recurringValue === "future",
          slug: authenticatedUser.slug ?? "",
          resourceId: schedule.resource?.id ?? ""
        });
        return res;
      });

      // allSettled, not all: cancelling a whole selection at once means one
      // failure would otherwise abort while its siblings had already been
      // cancelled, leaving the list and agenda showing bookings that are gone.
      // Every delete runs, then a failure is reported with what actually
      // happened.
      const results = await Promise.allSettled(promises);
      const failed = results.filter(
        (result) => result.status === "rejected" || !result.value.ok
      ).length;

      if (failed > 0) {
        const isDelegatedAccessMissing = results.some(
          (result) =>
            result.status === "rejected" &&
            isDelegatedAccessRequiredError(result.reason)
        );
        if (isDelegatedAccessMissing) {
          throw new Error(
            failed === results.length
              ? delegatedPermissionPartials.requiredError
              : `${failed} of ${results.length} reservations could not be cancelled — the rest were cancelled. ${delegatedPermissionPartials.requiredError}`
          );
        }
        throw new Error(
          failed === results.length
            ? "The reservations could not be cancelled — please try again"
            : `${failed} of ${results.length} reservations could not be cancelled — the rest were cancelled`
        );
      }
      return results;
    },

    // Runs whether or not every delete succeeded: some may have landed even
    // when others failed, so the caches have to be refreshed either way.
    onSettled: async () => {
      await invalidateSchedules();
    },

    onSuccess: () => {
      onCancelSuccess?.();
      onClose();
      toast.success("Reservation cancelled successfully");
    }
  });

  const handleConfirm = useCallback(async () => {
    try {
      await deleteMutation.mutateAsync();
      // Return undefined on success (no error to display)
      return undefined;
    } catch (error) {
      // Return error object for ConfirmModal to display
      return error;
    }
  }, [deleteMutation]);

  const handleCancel = useCallback(() => {
    // Called when cancel button is clicked
    setRecurringValue("this");
  }, []);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        // Reset value whenever modal closes (X, Escape, overlay click, or cancel button)
        setRecurringValue("this");
      }
      setOpen(isOpen);
    },
    [setOpen]
  );

  return (
    <ConfirmModal
      title={
        !isRecurring ? "Confirm Cancellation" : "Confirm Recurring Cancellation"
      }
      isOpen={open}
      onOpenChange={handleOpenChange}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      confirmLabel="Yes"
      cancelLabel="No"
      size="sm"
    >
      {!isRecurring ? (
        <p className="text-tertiary">
          {isSingle
            ? "Are you sure you would like to cancel this reservation?"
            : `Are you sure you would like to cancel these ${schedules.length} reservations?`}{" "}
        </p>
      ) : (
        <div className="mt-2 flex flex-col text-sm">
          {options.map((option) => (
            <Label key={option.value} className="my-1 block">
              <Input
                type="radio"
                onChange={() => {
                  setRecurringValue(option.value);
                }}
                checked={option.value === recurringValue}
              />
              <span className="ml-2">{option.label}</span>
            </Label>
          ))}
        </div>
      )}
    </ConfirmModal>
  );
}
